import { useEffect, useState } from "react";

import { useCardsQuery } from "@/api/cards";
import { ApiError } from "@/api/client";
import {
  useTenantCredentialsQuery,
  useTenantSettingsQuery,
  useUpdateTenantCredentialsMutation,
  useUpdateTenantSettingsMutation,
  type TenantSettings,
} from "@/api/settings";
import { Button, Pill, Ribbon } from "@/components/ui";
import { buildChangedSettingsValues, buildToggleOptions, parseFieldError } from "@/features/ayarlar/settingsDiff";
import { FieldShell, inputClass, NumberField, TextField, ToggleField } from "@/features/ayarlar/fields";
import { useIsMobile } from "@/hooks/useIsMobile";
import { t, type TrKey } from "@/i18n";
import { fmtDate } from "@/lib/utils";

type TabId = "marka" | "esikler" | "mesai" | "gorunum" | "ai" | "hedefler" | "baglantilar" | "senkron";

const TABS: TabId[] = ["marka", "esikler", "mesai", "gorunum", "ai", "hedefler", "baglantilar", "senkron"];

const TAB_LABEL_KEY: Record<TabId, TrKey> = {
  marka: "ayarlar.tabs.marka",
  esikler: "ayarlar.tabs.esikler",
  mesai: "ayarlar.tabs.mesai",
  gorunum: "ayarlar.tabs.gorunum",
  ai: "ayarlar.tabs.ai",
  hedefler: "ayarlar.tabs.hedefler",
  baglantilar: "ayarlar.tabs.baglantilar",
  senkron: "ayarlar.tabs.senkron",
};

/** Bu sayfanin yonettigi TUM ust-duzey noktali anahtarlar — sozlesme §2.
 * `alarm.*`/`ui.language`/`ticimax.cdn_tenant_id`/`ikas.*` KASITLI YOK
 * (gorev talimatinda listelenen sekmelerde yer almiyor). */
const MANAGED_KEYS = [
  "brand.logo_url",
  "brand.primary_color",
  "brand.accent_color",
  "thresholds.siparis_kargoya_saat",
  "thresholds.kargo_teslim_saat",
  "thresholds.stok_kritik",
  "thresholds.destek_cevap_saat",
  "schedule.depo",
  "schedule.kargo",
  "schedule.holidays",
  "ui.hidden_cards",
  "ui.hidden_tabs",
  "ai.enabled",
  "ai.tone",
  "ai.customer_display_name",
  "targets.monthly_revenue",
  "targets.monthly_orders",
  "ga4.property_id",
  "ads.meta_account_id",
  "sync.intervals",
  "sync.heavy_sources_enabled",
] as const;

const KEY_TAB: Record<string, TabId> = {
  "brand.logo_url": "marka",
  "brand.primary_color": "marka",
  "brand.accent_color": "marka",
  "thresholds.siparis_kargoya_saat": "esikler",
  "thresholds.kargo_teslim_saat": "esikler",
  "thresholds.stok_kritik": "esikler",
  "thresholds.destek_cevap_saat": "esikler",
  "schedule.depo": "mesai",
  "schedule.kargo": "mesai",
  "schedule.holidays": "mesai",
  "ui.hidden_cards": "gorunum",
  "ui.hidden_tabs": "gorunum",
  "ai.enabled": "ai",
  "ai.tone": "ai",
  "ai.customer_display_name": "ai",
  "targets.monthly_revenue": "hedefler",
  "targets.monthly_orders": "hedefler",
  "ga4.property_id": "baglantilar",
  "ads.meta_account_id": "baglantilar",
  "sync.intervals": "senkron",
  "sync.heavy_sources_enabled": "senkron",
};

/** ui.hidden_tabs secenekleri — capabilities.py TABS'ten "admin" HARIC
 * (useVisibleTabs /admin'i yalniz is_super'a gore acar, tabs[].visible'i
 * HIC OKUMAZ — orayı gizlemek no-op olurdu, bkz. gorev raporu). */
const HIDEABLE_TAB_OPTIONS: Array<{ id: string; labelKey: TrKey }> = [
  { id: "ozet", labelKey: "nav.ozet" },
  { id: "kpi", labelKey: "nav.kpi" },
  { id: "stok", labelKey: "nav.stok" },
  { id: "yorumlar", labelKey: "nav.yorumlar" },
  { id: "denetim", labelKey: "nav.denetim" },
];

/** sync.intervals icin duzenlenebilir kaynaklar — models.Source (sozlesme §2). */
const SYNC_SOURCES: Array<{ id: string; labelKey: TrKey }> = [
  { id: "orders", labelKey: "source.orders" },
  { id: "customers", labelKey: "source.customers" },
  { id: "products", labelKey: "source.products" },
  { id: "inventory_movements", labelKey: "source.inventory_movements" },
  { id: "product_sales_report", labelKey: "source.product_sales_report" },
  { id: "returns", labelKey: "source.returns" },
  { id: "refunds", labelKey: "source.refunds" },
  { id: "tickets", labelKey: "source.tickets" },
  { id: "reviews", labelKey: "source.reviews" },
  { id: "abandoned_carts", labelKey: "source.abandoned_carts" },
  { id: "ads_meta", labelKey: "source.ads_meta" },
  { id: "ads_google", labelKey: "source.ads_google" },
];

const AI_TONES: Array<{ id: string; labelKey: TrKey }> = [
  { id: "yonetici", labelKey: "ayarlar.ai.tone.yonetici" },
  { id: "resmi", labelKey: "ayarlar.ai.tone.resmi" },
  { id: "samimi", labelKey: "ayarlar.ai.tone.samimi" },
];

interface ScheduleValue {
  weekday_start: string;
  weekday_end: string;
  saturday_works: boolean;
  saturday_start: string;
  saturday_end: string;
  sunday_works: boolean;
}

function asSchedule(value: unknown): ScheduleValue {
  const v = (value ?? {}) as Partial<ScheduleValue>;
  return {
    weekday_start: v.weekday_start ?? "09:00",
    weekday_end: v.weekday_end ?? "17:00",
    saturday_works: Boolean(v.saturday_works),
    saturday_start: v.saturday_start ?? "09:00",
    saturday_end: v.saturday_end ?? "13:00",
    sunday_works: Boolean(v.sunday_works),
  };
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((x): x is string => typeof x === "string") : [];
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

/** /ayarlar — tenant_admin (gorev talimati madde 2). Tum sekmeler TEK bir
 * yerel taslak (draft) uzerinde calisir; "Kaydet" yalniz DEGISEN ust-duzey
 * anahtarlari PUT eder (buildChangedSettingsValues). */
export function Ayarlar() {
  // INCELEME BULGUSU (arayuz-denetim madde 1, duzeltme turu): bu cubuk
  // position:fixed oldugundan Layout'un main padding-bottom'u (--mobile-nav-h)
  // ONU ETKILEMEZ — mobilde MobileNav ile AYNI bottom:0'da durup onu
  // ORTUYORDU (kullanici Kaydet'e bastigini sanip nav'a yonlendiriliyordu).
  // Mobilde cubugu MobileNav'in tam ustune (bottom: var(--mobile-nav-h))
  // tasiyoruz; masaustunde MobileNav olmadigi icin bottom-0 kalir.
  const isMobile = useIsMobile();
  const settingsQuery = useTenantSettingsQuery();
  const updateSettings = useUpdateTenantSettingsMutation();
  const credentialsQuery = useTenantCredentialsQuery();
  const updateCredentials = useUpdateTenantCredentialsMutation();
  const cardsQuery = useCardsQuery();

  const [activeTab, setActiveTab] = useState<TabId>("marka");
  const [draft, setDraft] = useState<TenantSettings | null>(null);
  const [metaToken, setMetaToken] = useState("");
  const [fieldError, setFieldError] = useState<{ field: string | null; message: string } | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saved" | "no_changes">("idle");

  useEffect(() => {
    if (settingsQuery.data && draft === null) setDraft(settingsQuery.data);
  }, [settingsQuery.data, draft]);

  function setField(key: string, value: unknown) {
    setSaveState("idle");
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    if (fieldError?.field === key) setFieldError(null);
  }

  function resetField(key: string) {
    updateSettings.mutate(
      { [key]: null },
      {
        onSuccess: (res) => {
          // INCELEME BULGUSU: `setDraft(res)` TUM taslagi sunucunun donus
          // yanitiyla degistiriyordu — `res` yalniz PUT edilen `{[key]:null}`
          // uygulanmis TAM ayar kumesidir, o an baska bir sekmede yapilmis
          // HENUZ KAYDEDILMEMIS diger taslak degisiklikleri ICERMEZ; bu
          // yuzden "Varsayılana dön" kullanicinin ilgisiz alanlardaki
          // yazilmamis degisikliklerini SESSIZCE siliyordu. Yalniz SIFIRLANAN
          // alan sunucu degeriyle guncellenir, geri kalan taslak KORUNUR.
          setDraft((d) => (d ? { ...d, [key]: res[key] } : d));
          setFieldError(null);
        },
      },
    );
  }

  async function handleSave() {
    setSaveState("idle");
    setFieldError(null);
    if (!draft || !settingsQuery.data) return;

    const changed = buildChangedSettingsValues(settingsQuery.data, draft, MANAGED_KEYS);
    const hasSettingsChange = Object.keys(changed).length > 0;
    const hasTokenChange = metaToken.trim().length > 0;

    if (!hasSettingsChange && !hasTokenChange) {
      setSaveState("no_changes");
      return;
    }

    try {
      if (hasSettingsChange) {
        const res = await updateSettings.mutateAsync(changed);
        setDraft(res);
      }
      if (hasTokenChange) {
        await updateCredentials.mutateAsync({ meta_access_token: metaToken.trim() });
        setMetaToken("");
      }
      setSaveState("saved");
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const parsed = parseFieldError(err.message, MANAGED_KEYS);
        setFieldError(parsed);
        if (parsed.field && KEY_TAB[parsed.field]) setActiveTab(KEY_TAB[parsed.field]);
      } else {
        setFieldError({ field: null, message: err instanceof ApiError ? err.message : t("ayarlar.save_error") });
      }
    }
  }

  // INCELEME BULGUSU: eskiden `isLoading || draft === null` TEK kosulda
  // birlestirilmisti — GET /tenant/settings hata verdiginde `settingsQuery.data`
  // hep undefined kalir, ustteki useEffect draft'i HICBIR ZAMAN doldurmaz, bu
  // yuzden `draft === null` SONSUZA DEK true kalip asagidaki `isError`
  // dalina HIC ULASILAMAZDI ("Yükleniyor…" kilitlenirdi). Once isLoading,
  // sonra isError, en son (hata yok ama efekt henuz calismadi) draft===null
  // sirasiyla kontrol edilir.
  if (settingsQuery.isLoading) {
    return <p className="text-sm text-muted">{t("common.loading")}</p>;
  }
  if (settingsQuery.isError) {
    return (
      <p role="alert" className="text-sm text-red">
        {t("common.error")}
      </p>
    );
  }
  if (draft === null) {
    return <p className="text-sm text-muted">{t("common.loading")}</p>;
  }

  const isSaving = updateSettings.isPending || updateCredentials.isPending;
  const hiddenCards = asStringArray(draft["ui.hidden_cards"]);
  const hiddenTabs = asStringArray(draft["ui.hidden_tabs"]);
  const cardOptions = buildToggleOptions(
    (cardsQuery.data?.cards ?? []).map((c) => ({ id: c.id, title: c.title })),
    hiddenCards,
  );

  function toggleInList(key: string, list: string[], id: string, checked: boolean) {
    setField(key, checked ? [...list, id] : list.filter((x) => x !== id));
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4 pb-28">
      <div>
        <h1 className="font-display text-xl font-bold text-ink">{t("ayarlar.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("ayarlar.subtitle")}</p>
      </div>

      <div role="tablist" aria-label={t("ayarlar.title")} className="flex flex-wrap gap-1 border-b-[1.5px] border-line">
        {TABS.map((tabId) => (
          <button
            key={tabId}
            type="button"
            role="tab"
            aria-selected={activeTab === tabId}
            onClick={() => setActiveTab(tabId)}
            className={`main-tab ${activeTab === tabId ? "main-tab-active" : ""}`}
          >
            {t(TAB_LABEL_KEY[tabId])}
          </button>
        ))}
      </div>

      {fieldError && !fieldError.field && <Ribbon tone="bad">{fieldError.message}</Ribbon>}

      <div role="tabpanel" className="card">
        {activeTab === "marka" && (
          <div className="space-y-4">
            <TextField
              label={t("ayarlar.marka.logo_url")}
              settingKey="brand.logo_url"
              type="url"
              value={String(draft["brand.logo_url"] ?? "")}
              onChange={(v) => setField("brand.logo_url", v)}
              onReset={() => resetField("brand.logo_url")}
              error={fieldError?.field === "brand.logo_url" ? fieldError.message : null}
            />
            <TextField
              label={t("ayarlar.marka.primary_color")}
              settingKey="brand.primary_color"
              type="color"
              value={String(draft["brand.primary_color"] ?? "#1A56DB")}
              onChange={(v) => setField("brand.primary_color", v)}
              onReset={() => resetField("brand.primary_color")}
              error={fieldError?.field === "brand.primary_color" ? fieldError.message : null}
            />
            <TextField
              label={t("ayarlar.marka.accent_color")}
              settingKey="brand.accent_color"
              type="color"
              value={String(draft["brand.accent_color"] ?? "#c33434")}
              onChange={(v) => setField("brand.accent_color", v)}
              onReset={() => resetField("brand.accent_color")}
              error={fieldError?.field === "brand.accent_color" ? fieldError.message : null}
            />
            <div className="rounded-[9px] border-[1.5px] border-line bg-bg p-4">
              <p className="mb-2 text-xs font-semibold uppercase text-muted">{t("ayarlar.marka.preview")}</p>
              <div
                className="flex items-center gap-2 rounded-lg p-3 text-white"
                style={{ backgroundColor: String(draft["brand.primary_color"] ?? "#1A56DB") }}
              >
                <span
                  className="grid h-8 w-8 place-items-center rounded-lg text-sm font-bold"
                  style={{ backgroundColor: String(draft["brand.accent_color"] ?? "#c33434") }}
                >
                  A
                </span>
                <span className="font-semibold">{t("ayarlar.marka.preview_tenant")}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === "esikler" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              label={t("ayarlar.esikler.siparis_kargoya_saat")}
              settingKey="thresholds.siparis_kargoya_saat"
              min={0}
              value={asNumber(draft["thresholds.siparis_kargoya_saat"])}
              onChange={(v) => setField("thresholds.siparis_kargoya_saat", v)}
              onReset={() => resetField("thresholds.siparis_kargoya_saat")}
              error={fieldError?.field === "thresholds.siparis_kargoya_saat" ? fieldError.message : null}
            />
            <NumberField
              label={t("ayarlar.esikler.kargo_teslim_saat")}
              settingKey="thresholds.kargo_teslim_saat"
              min={0}
              value={asNumber(draft["thresholds.kargo_teslim_saat"])}
              onChange={(v) => setField("thresholds.kargo_teslim_saat", v)}
              onReset={() => resetField("thresholds.kargo_teslim_saat")}
              error={fieldError?.field === "thresholds.kargo_teslim_saat" ? fieldError.message : null}
            />
            <NumberField
              label={t("ayarlar.esikler.stok_kritik")}
              settingKey="thresholds.stok_kritik"
              min={0}
              value={asNumber(draft["thresholds.stok_kritik"])}
              onChange={(v) => setField("thresholds.stok_kritik", v)}
              onReset={() => resetField("thresholds.stok_kritik")}
              error={fieldError?.field === "thresholds.stok_kritik" ? fieldError.message : null}
            />
            <NumberField
              label={t("ayarlar.esikler.destek_cevap_saat")}
              settingKey="thresholds.destek_cevap_saat"
              min={0}
              value={asNumber(draft["thresholds.destek_cevap_saat"])}
              onChange={(v) => setField("thresholds.destek_cevap_saat", v)}
              onReset={() => resetField("thresholds.destek_cevap_saat")}
              error={fieldError?.field === "thresholds.destek_cevap_saat" ? fieldError.message : null}
            />
          </div>
        )}

        {activeTab === "mesai" && (
          <div className="space-y-6">
            <ScheduleEditor
              title={t("ayarlar.mesai.depo")}
              settingKey="schedule.depo"
              value={asSchedule(draft["schedule.depo"])}
              onChange={(v) => setField("schedule.depo", v)}
              onReset={() => resetField("schedule.depo")}
              error={fieldError?.field === "schedule.depo" ? fieldError.message : null}
            />
            <ScheduleEditor
              title={t("ayarlar.mesai.kargo")}
              settingKey="schedule.kargo"
              value={asSchedule(draft["schedule.kargo"])}
              onChange={(v) => setField("schedule.kargo", v)}
              onReset={() => resetField("schedule.kargo")}
              error={fieldError?.field === "schedule.kargo" ? fieldError.message : null}
            />
            <FieldShell
              label={t("ayarlar.mesai.holidays")}
              settingKey="schedule.holidays"
              hint={t("ayarlar.mesai.holidays_hint")}
              onReset={() => resetField("schedule.holidays")}
              error={fieldError?.field === "schedule.holidays" ? fieldError.message : null}
            >
              <input
                id="schedule.holidays"
                value={asStringArray(draft["schedule.holidays"]).join(", ")}
                onChange={(e) =>
                  setField(
                    "schedule.holidays",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                className={inputClass}
              />
            </FieldShell>
          </div>
        )}

        {activeTab === "gorunum" && (
          <div className="space-y-6">
            <div>
              <p className="mb-2 text-sm font-medium text-ink">{t("ayarlar.gorunum.hidden_cards")}</p>
              <p className="mb-2 text-xs text-muted">{t("ayarlar.gorunum.hint")}</p>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {cardOptions.map((card) => (
                  <label key={card.id} className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={hiddenCards.includes(card.id)}
                      onChange={(e) => toggleInList("ui.hidden_cards", hiddenCards, card.id, e.target.checked)}
                      className="h-4 w-4"
                    />
                    {card.title}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-ink">{t("ayarlar.gorunum.hidden_tabs")}</p>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {HIDEABLE_TAB_OPTIONS.map((tab) => (
                  <label key={tab.id} className="flex min-h-[44px] items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={hiddenTabs.includes(tab.id)}
                      onChange={(e) => toggleInList("ui.hidden_tabs", hiddenTabs, tab.id, e.target.checked)}
                      className="h-4 w-4"
                    />
                    {t(tab.labelKey)}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "ai" && (
          <div className="space-y-4">
            <ToggleField
              label={t("ayarlar.ai.enabled")}
              settingKey="ai.enabled"
              checked={Boolean(draft["ai.enabled"])}
              onChange={(v) => setField("ai.enabled", v)}
            />
            <FieldShell
              label={t("ayarlar.ai.tone")}
              settingKey="ai.tone"
              error={fieldError?.field === "ai.tone" ? fieldError.message : null}
            >
              <select
                id="ai.tone"
                value={String(draft["ai.tone"] ?? "yonetici")}
                onChange={(e) => setField("ai.tone", e.target.value)}
                className={inputClass}
              >
                {AI_TONES.map((tone) => (
                  <option key={tone.id} value={tone.id}>
                    {t(tone.labelKey)}
                  </option>
                ))}
              </select>
            </FieldShell>
            <TextField
              label={t("ayarlar.ai.customer_display_name")}
              settingKey="ai.customer_display_name"
              value={String(draft["ai.customer_display_name"] ?? "")}
              onChange={(v) => setField("ai.customer_display_name", v)}
              onReset={() => resetField("ai.customer_display_name")}
              error={fieldError?.field === "ai.customer_display_name" ? fieldError.message : null}
            />
          </div>
        )}

        {activeTab === "hedefler" && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              label={t("ayarlar.hedefler.monthly_revenue")}
              settingKey="targets.monthly_revenue"
              min={0}
              value={asNumber(draft["targets.monthly_revenue"])}
              onChange={(v) => setField("targets.monthly_revenue", v)}
              onReset={() => resetField("targets.monthly_revenue")}
              error={fieldError?.field === "targets.monthly_revenue" ? fieldError.message : null}
            />
            <NumberField
              label={t("ayarlar.hedefler.monthly_orders")}
              settingKey="targets.monthly_orders"
              min={0}
              value={asNumber(draft["targets.monthly_orders"])}
              onChange={(v) => setField("targets.monthly_orders", v)}
              onReset={() => resetField("targets.monthly_orders")}
              error={fieldError?.field === "targets.monthly_orders" ? fieldError.message : null}
            />
          </div>
        )}

        {activeTab === "baglantilar" && (
          <div className="space-y-4">
            <TextField
              label={t("ayarlar.baglantilar.ga4_property_id")}
              settingKey="ga4.property_id"
              value={String(draft["ga4.property_id"] ?? "")}
              onChange={(v) => setField("ga4.property_id", v)}
              onReset={() => resetField("ga4.property_id")}
              hint={t("ayarlar.baglantilar.ga4_hint")}
              error={fieldError?.field === "ga4.property_id" ? fieldError.message : null}
            />
            <TextField
              label={t("ayarlar.baglantilar.meta_account_id")}
              settingKey="ads.meta_account_id"
              value={String(draft["ads.meta_account_id"] ?? "")}
              onChange={(v) => setField("ads.meta_account_id", v)}
              onReset={() => resetField("ads.meta_account_id")}
              error={fieldError?.field === "ads.meta_account_id" ? fieldError.message : null}
            />
            <FieldShell
              label={t("ayarlar.baglantilar.meta_access_token")}
              hint={t("ayarlar.baglantilar.meta_token_hint")}
            >
              <input
                type="password"
                autoComplete="off"
                value={metaToken}
                onChange={(e) => setMetaToken(e.target.value)}
                className={inputClass}
              />
              {credentialsQuery.data?.some((k) => k.key === "meta_access_token") && (
                <Pill tone="info" className="mt-1.5">
                  {t("ayarlar.baglantilar.meta_token_current", {
                    date: fmtDate(credentialsQuery.data.find((k) => k.key === "meta_access_token")?.updated_at ?? null),
                  })}
                </Pill>
              )}
            </FieldShell>
          </div>
        )}

        {activeTab === "senkron" && (
          <div className="space-y-4">
            <ToggleField
              label={t("ayarlar.senkron.heavy_sources_enabled")}
              settingKey="sync.heavy_sources_enabled"
              checked={Boolean(draft["sync.heavy_sources_enabled"])}
              onChange={(v) => setField("sync.heavy_sources_enabled", v)}
            />
            <div>
              <p className="mb-2 text-sm font-medium text-ink">{t("ayarlar.senkron.intervals_title")}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {SYNC_SOURCES.map((source) => {
                  const intervals = (draft["sync.intervals"] ?? {}) as Record<string, number>;
                  return (
                    <label key={source.id} className="flex items-center justify-between gap-2 text-sm text-ink">
                      {t(source.labelKey)}
                      <input
                        type="number"
                        min={1}
                        value={intervals[source.id] ?? ""}
                        onChange={(e) => {
                          const next = { ...intervals };
                          if (e.target.value === "") delete next[source.id];
                          else next[source.id] = Number(e.target.value);
                          setField("sync.intervals", next);
                        }}
                        className="input !w-24 !px-2"
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        data-testid="ayarlar-save-bar"
        className={`card fixed z-30 flex flex-wrap items-center gap-3 shadow-modal ${
          isMobile ? "inset-x-0 bottom-[var(--mobile-nav-h)] !rounded-none border-x-0 border-b-0" : "bottom-4 right-6"
        }`}
      >
        <Button type="button" variant="primary" disabled={isSaving} onClick={() => void handleSave()}>
          {isSaving ? t("ayarlar.saving") : t("ayarlar.save")}
        </Button>
        {saveState === "saved" && <Pill tone="ok">{t("ayarlar.saved")}</Pill>}
        {saveState === "no_changes" && <Pill tone="muted">{t("ayarlar.no_changes")}</Pill>}
      </div>
    </section>
  );
}

interface ScheduleEditorProps {
  title: string;
  settingKey: string;
  value: ScheduleValue;
  onChange: (value: ScheduleValue) => void;
  onReset: () => void;
  error?: string | null;
}

function ScheduleEditor({ title, settingKey, value, onChange, onReset, error }: ScheduleEditorProps) {
  function set<K extends keyof ScheduleValue>(field: K, fieldValue: ScheduleValue[K]) {
    onChange({ ...value, [field]: fieldValue });
  }

  return (
    <div data-field={settingKey} className="rounded-[9px] border-[1.5px] border-line bg-bg p-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <Button type="button" variant="link" size="sm" onClick={onReset} className="!min-h-[36px]">
          {t("ayarlar.reset_field")}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label className="text-xs text-muted">
          {t("ayarlar.mesai.weekday_start")}
          <input
            type="time"
            value={value.weekday_start}
            onChange={(e) => set("weekday_start", e.target.value)}
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="text-xs text-muted">
          {t("ayarlar.mesai.weekday_end")}
          <input
            type="time"
            value={value.weekday_end}
            onChange={(e) => set("weekday_end", e.target.value)}
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={value.saturday_works}
            onChange={(e) => set("saturday_works", e.target.checked)}
            className="h-4 w-4"
          />
          {t("ayarlar.mesai.saturday_works")}
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={value.sunday_works}
            onChange={(e) => set("sunday_works", e.target.checked)}
            className="h-4 w-4"
          />
          {t("ayarlar.mesai.sunday_works")}
        </label>
        <label className="text-xs text-muted">
          {t("ayarlar.mesai.saturday_start")}
          <input
            type="time"
            value={value.saturday_start}
            onChange={(e) => set("saturday_start", e.target.value)}
            className={`${inputClass} mt-1`}
          />
        </label>
        <label className="text-xs text-muted">
          {t("ayarlar.mesai.saturday_end")}
          <input
            type="time"
            value={value.saturday_end}
            onChange={(e) => set("saturday_end", e.target.value)}
            className={`${inputClass} mt-1`}
          />
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red">
          {error}
        </p>
      )}
    </div>
  );
}
